import { notifyProgress } from '@billy/design-system'
import { pdf } from '@react-pdf/renderer'
import download from 'downloadjs'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { VatDeclarationsPeriod } from '@features/vatDeclarations/types/vatDeclarationsPeriod'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { NotificationKeys } from '../../../../../../../enums/notificationKeys'
import { downloadStatusToTKeys } from '../../../../../../../utils/export/downloadStatusToTKeys'
import { sanitizeFileName } from '../../../../../../../utils/export/sanitizeFileName'
import { useFetchVatDeclarationAuditTrail } from '../../../../../hooks/useFetchVatDeclarationAuditTrail'
import { getPeriodData } from '../../../../../utils/getPeriodData'
import { buildPdfDocument } from '../elements/exports/pdf/utils/buildPdfDocument'

interface UsePdfExportProps {
  vatDeclaration?: VatDeclarationsPeriod
  withAuditTrail?: boolean
}

export const usePdfExport = ({ vatDeclaration, withAuditTrail }: UsePdfExportProps) => {
  const { t } = useTranslation()

  const [isLoading, setIsLoading] = useState(false)
  const { organization } = useUserOrganization()
  const periodData = getPeriodData(vatDeclaration?.period || '')

  const { data: auditTrailData } = useFetchVatDeclarationAuditTrail({
    showVoided: true,
    vatDeclarationId: vatDeclaration?.id,
  })

  const { name, countryId, baseCurrencyId, localeId } = organization || {}

  const document = useMemo(() => {
    return buildPdfDocument({
      auditTrailData,
      countryId,
      currency: baseCurrencyId,
      data: vatDeclaration,
      locale: localeId,
      organizationName: name || '',
      withAuditTrail,
    })
  }, [auditTrailData, countryId, baseCurrencyId, vatDeclaration, localeId, name, withAuditTrail])

  const exportAsPdf = useCallback(async () => {
    try {
      if (!document) {
        return
      }
      setIsLoading(true)

      const { periodSettled, year } = periodData || {}
      const periodText = periodSettled && year ? `-${periodSettled}-${year}` : ''

      const blob = await pdf(document).toBlob()
      const finalName = `${t('vat_declaration.export.file_name')}-${sanitizeFileName(name || '')}${periodText}.pdf`
      download(blob, finalName)

      const successTKeys = downloadStatusToTKeys.success

      notifyProgress(
        {
          id: NotificationKeys.VatDeclarationsExport,
          title: t(successTKeys.title),
          description: t(successTKeys.description),
          variant: 'success',
          progress: 100,
          closable: true,
        },
        { autoClose: 30000 },
      )
      setIsLoading(false)
    } catch {
      const errorTKeys = downloadStatusToTKeys.error

      setIsLoading(false)
      notifyProgress({
        id: NotificationKeys.VatDeclarationsExport,
        title: t(errorTKeys.title),
        description: t(errorTKeys.description),
        variant: 'error',
        progress: 100,
        closable: true,
      })
    }
  }, [document, name, periodData, t])

  return { exportAsPdf, isLoading }
}
